import { useState, useMemo, useEffect } from "react";
import {
  FileText, Image as ImageIcon, Download, MapPin, Trash2, Pencil, ClipboardCheck,
  MessageCircle, History, ArrowRight, AlertCircle, Filter, X, Paperclip, ShieldCheck, Percent,
  } from "lucide-react";
import TabelasComerciais from "../components/TabelasComerciais";
import GuideBanner from "../components/GuideBanner";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import ConfirmDialog from "../components/ConfirmDialog";
import Modal from "../components/Modal";
import FichaDetalhe from "../modals/FichaDetalhe";
import { unificarRede, motivoDaRecusa, conversaValidada } from "../lib/rede";
import { PARTNER_STAGES } from "../constants";
import {
  SISTEMAS, TIPOS_ESTABELECIMENTO, TIPOS_VEICULO, BRAZILIAN_STATES,
  SERVICOS_PRESTADOS, ASSISTENCIA_TIPOS, servicoLabel, resumoAtendimento, resumoPerfil,
} from "../constants";
import { getSignedUrl, removeFromStorage } from "../lib/storage";
import { credStatus, waLink, getToday, exportCSV, fmtDate, fmtDataHora } from "../utils";

const STATUS_LABEL = { pendente: "Pendente", andamento: "Em andamento", concluido: "Concluída", sem_interesse: "Sem interesse" };
const ORIGEM_LABEL = { link: "Link", contato: "Registro rápido", visita: "Visita" };

const STATUS_OPTS = [
  { id: "all", label: "Todos os status" },
  { id: "pendente", label: "⏳ Pendente" },
  { id: "andamento", label: "🔄 Em andamento" },
  { id: "concluido", label: "✅ Concluída" },
  { id: "sem_interesse", label: "🚫 Sem interesse" },
];

const PERIODOS = [
  { id: "all", label: "Qualquer data" },
  { id: "hoje", label: "Hoje" },
  { id: "7", label: "Últimos 7 dias" },
  { id: "30", label: "Últimos 30 dias" },
];

// O que os sistemas do fornecedor ainda esperam dele, lido do que a serverless
// gravou na ficha. Devolve o primeiro sistema com documento pendente E link
// pronto — é o que dá para cobrar agora, sem abrir a ficha.
const pendenciaDoFornecedor = (c) => {
  const sync = c.integracaoSync;
  if (!sync || typeof sync !== "object") return null;
  for (const [sistema, s] of Object.entries(sync)) {
    const faltam = Array.isArray(s?.docs_faltantes) ? s.docs_faltantes : [];
    if (faltam.length) return { sistema, faltam, url: s.upload_url || null };
  }
  return null;
};

const diasAtras = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};


const ACAO_LABEL = {
  criacao: "Ficha criada",
  edicao: "Dados atualizados",
  assinatura: "Assinatura coletada",
  anexo: "Documento anexado",
  remocao: "Documento removido",
  conclusao: "Credenciamento concluído",
  sem_interesse: "Sem interesse registrado",
  retomada: "Contato retomado",
};
const ACAO_COLOR = {
  criacao: "#6366f1", edicao: "#3b82f6", assinatura: "#0d9488",
  anexo: "#7c3aed", remocao: "#ef4444", conclusao: "#059669",
  sem_interesse: "#d97706", retomada: "#7c3aed",
};

export default function Credenciamentos({ credenciamentos = [], partners = [], search, onContinuarRascunho, onEdit, onIrParaRede, setCredenciamentos, userName = "", onVerNaRede }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [sistemaFilter, setSistemaFilter] = useState("all");
  const [ufFilter, setUfFilter] = useState("all");
  const [cidadeFilter, setCidadeFilter] = useState("");
  const [respFilter, setRespFilter] = useState("");
  const [espFilter, setEspFilter] = useState("all");
  const [socorroFilter, setSocorroFilter] = useState("all"); // all | sim | 24h | rodovias | nao
  const [periodoFilter, setPeriodoFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recente");
  const [showFilters, setShowFilters] = useState(false);
  const [verTabelas, setVerTabelas] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [toDelete, setToDelete] = useState(null);
  const [histOf, setHistOf] = useState(null);
  const [draftInfo, setDraftInfo] = useState(null);

  useEffect(() => {
    try {
      const d = localStorage.getItem("cred_draft_v1");
      if (d) { const o = JSON.parse(d); if (o && (o.razaoSocial || o.nomeFantasia || o.cnpj)) setDraftInfo(o); }
    } catch { /* ignora */ }
  }, []);

  const [detailOf, setDetailOf] = useState(null);

  const descartarDraft = () => {
    try { localStorage.removeItem("cred_draft_v1"); localStorage.removeItem("cred_draft_v1_docs"); localStorage.removeItem("cred_draft_v1_sig"); } catch { /* ignora */ }
    setDraftInfo(null);
  };

  // ── KPIs (sobre todas as fichas) ──
  // A mesma junção da Rede, para a ficha saber de que empresa ela é. Repetir
  // a regra aqui abriria espaço para as duas telas discordarem de novo.
  const parceiroDaFicha = useMemo(() => {
    const mapa = {};
    for (const item of unificarRede(partners, credenciamentos)) {
      if (item.ficha && item.parceiro) mapa[item.ficha.id] = item.parceiro;
    }
    return mapa;
  }, [partners, credenciamentos]);

  const kpis = useMemo(() => {
    const hoje = getToday();
    const mes = hoje.slice(0, 7);
    let concl = 0, andamento = 0, pend = 0, semInteresse = 0, mesCount = 0;
    for (const c of credenciamentos) {
      const s = credStatus(c).id;
      if (s === "concluido") concl++;
      else if (s === "sem_interesse") semInteresse++;
      else if (s === "andamento") andamento++;
      else pend++;
      if ((c.createdAt || "").slice(0, 7) === mes) mesCount++;
    }
    return { total: credenciamentos.length, concl, andamento, pend, semInteresse, mesCount };
  }, [credenciamentos]);

  // UFs presentes nos dados
  const ufOptions = useMemo(() => {
    const set = new Set(credenciamentos.map((c) => c.estado).filter(Boolean));
    return Array.from(set).sort();
  }, [credenciamentos]);

  const filtered = useMemo(() => {
    setPage(1);
    const texto = (q || search || "").toLowerCase().trim();
    const cutoff = periodoFilter === "hoje" ? getToday() : periodoFilter !== "all" ? diasAtras(Number(periodoFilter)) : null;
    const list = credenciamentos.filter((c) => {
      const st = credStatus(c).id;
      if (statusFilter !== "all" && st !== statusFilter) return false;
      if (sistemaFilter !== "all" && !(c.sistemas || []).includes(sistemaFilter)) return false;
      if (ufFilter !== "all" && c.estado !== ufFilter) return false;
      if (cidadeFilter && !(c.cidade || "").toLowerCase().includes(cidadeFilter.toLowerCase())) return false;
      if (respFilter && !(c.responsavelNome || "").toLowerCase().includes(respFilter.toLowerCase())) return false;
      if (espFilter !== "all" && !(c.servicos || []).includes(Number(espFilter))) return false;
      if (socorroFilter === "sim" && !c.fazSocorro) return false;
      if (socorroFilter === "24h" && !c.atende24h) return false;
      if (socorroFilter === "rodovias" && !c.assistenciaRodovias) return false;
      if (socorroFilter === "nao" && c.fazSocorro) return false;
      if (cutoff) {
        const cd = (c.createdAt || "");
        if (periodoFilter === "hoje" ? cd !== cutoff : cd < cutoff) return false;
      }
      if (texto) {
        const hay = `${c.razaoSocial} ${c.nomeFantasia} ${c.cnpj} ${c.responsavelNome} ${c.cidade}`.toLowerCase();
        if (!hay.includes(texto)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      const av = `${a.createdAt || ""}${a.id}`, bv = `${b.createdAt || ""}${b.id}`;
      return sort === "recente" ? bv.localeCompare(av) : av.localeCompare(bv);
    });
    return list;
  }, [credenciamentos, statusFilter, sistemaFilter, ufFilter, cidadeFilter, respFilter, espFilter, socorroFilter, periodoFilter, q, search, sort]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const handleExport = () => {
    const data = filtered.map((c) => {
      const st = credStatus(c);
      return {
        "Nome Fantasia": c.nomeFantasia || "", "Razão Social": c.razaoSocial || "",
        CNPJ: c.cnpj || "", "Inscrição Estadual": c.inscricaoEstadual || "",
        Status: STATUS_LABEL[st.id] || st.id, Pendências: (st.pend || []).join(", "),
        Tipo: TIPOS_ESTABELECIMENTO.find((t) => t.id === c.tipoEstabelecimento)?.label || "",
        "Ramo de Atividade": c.atividade || "", "Simples Nacional": c.simplesNacional === "sim" ? "Sim" : c.simplesNacional === "nao" ? "Não" : "",
        Cidade: c.cidade || "", Estado: c.estado || "",
        Telefone: c.telefone || "", Celular: c.celular || "", "E-mail": c.email || "",
        Responsável: c.responsavelNome || "", "CPF/RG": c.responsavelDoc || "", "Cargo": c.responsavelCargo || "",
        "Representante legal": c.representanteNome || "", "Função do representante": c.representanteCargo || "",
        "Documentos pendentes": (st.docsPend || []).map((d) => d.label).join(", "),
        Banco: c.banco || "", Agência: c.agencia || "", Conta: c.contaCorrente || "",
        "Tipo de conta": c.tipoConta === "poupanca" ? "Poupança" : c.tipoConta === "corrente" ? "Corrente" : "",
        "Titular da conta": c.titularConta || "", "Chave PIX": c.chavePix || "",
        "Serviços prestados": (c.servicos || []).map(servicoLabel).filter(Boolean).join(", "),
        "Veículos atendidos": (c.tiposVeiculo || []).map((id) => TIPOS_VEICULO.find((t) => t.id === id)?.label).filter(Boolean).join(", "),
        // Uma coluna só para o que varia por ramo — abrir uma coluna por campo
        // deixaria a planilha cheia de vazios para quem não é do ramo.
        "Perfil do ramo": resumoPerfil(c.tipoEstabelecimento, c.perfil).map((l) => `${l.k}: ${l.v}`).join(" | "),
        "Socorro/assistência": c.fazSocorro ? "Sim" : "Não",
        "Tipos de assistência": (c.tiposAssistencia || []).map((id) => ASSISTENCIA_TIPOS.find((a) => a.id === id)?.label).filter(Boolean).join(", "),
        "Atende 24h": c.atende24h ? "Sim" : "Não",
        "Horário de atendimento": c.fazSocorro ? resumoAtendimento(c) : "",
        "Assistência em rodovias": c.assistenciaRodovias ? "Sim" : "Não",
        "Rodovias atendidas": c.rodoviasAtendidas || "",
        "Telefone de plantão": c.telefoneSocorro || "",
        Sistemas: (c.sistemas || []).map((id) => SISTEMAS.find((s) => s.id === id)?.label || id).join(", "),
        Origem: ORIGEM_LABEL[c.origem] || "Visita", Dispositivo: c.dispositivo || "",
        Criado: fmtDate(c.createdAt),
      };
    });
    exportCSV(data, "fichas_credenciamento_instasolutions");
  };

  const openFile = async (path) => {
    if (!path) return;
    const win = window.open("", "_blank");
    const url = await getSignedUrl(path);
    if (!url) { if (win) win.close(); return; }
    if (win) win.location.href = url; else window.location.href = url;
  };

  const confirmDelete = async () => {
    const c = toDelete;
    setToDelete(null);
    if (!c) return;
    const paths = [c.fichaPath, c.assinaturaPath, ...(c.documentos || []).map((d) => d.path)].filter(Boolean);
    try { await removeFromStorage(paths); } catch (e) { console.warn(e); }
    setCredenciamentos((prev) => prev.filter((x) => x.id !== c.id));
  };

  const limparFiltros = () => {
    setStatusFilter("all"); setSistemaFilter("all"); setUfFilter("all");
    setCidadeFilter(""); setRespFilter(""); setEspFilter("all"); setSocorroFilter("all"); setPeriodoFilter("all"); setQ("");
  };
  const filtrosAtivos = statusFilter !== "all" || sistemaFilter !== "all" || ufFilter !== "all" || cidadeFilter || respFilter || espFilter !== "all" || socorroFilter !== "all" || periodoFilter !== "all" || q;

  const sel = { padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit", width: "100%" };
  const inp = { ...sel, cursor: "text" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Fichas — a esteira de quem já aceitou"
        text="O contato começa na Rede de Parceiros; quando a empresa aceita, o trabalho continua aqui: documentos pendentes, links, Cartão CNPJ, vínculo com os três sistemas e a conclusão. Cada ficha mostra em que estágio da rede a empresa está — clique no selo para abri-la lá."
      />

      {draftInfo && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "10px 14px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: 12, color: "#92400e" }}>
            <strong>Rascunho não salvo</strong> — {draftInfo.nomeFantasia || draftInfo.razaoSocial || draftInfo.cnpj}. Continue de onde parou (dados, assinatura e fotos preservados).
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setDraftInfo(null); onContinuarRascunho && onContinuarRascunho(); }} style={{ padding: "6px 14px", borderRadius: 7, border: "none", background: "#d97706", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Continuar</button>
            <button onClick={descartarDraft} style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid #fcd34d", background: "#fff", color: "#92400e", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Descartar</button>
          </div>
        </div>
      )}

      {/* ── Painel de indicadores ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <KpiCard label="📊 Total de fichas" value={kpis.total} color="#7c3aed" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
        <KpiCard label="✅ Concluídas" value={kpis.concl} color="#059669" active={statusFilter === "concluido"} onClick={() => setStatusFilter("concluido")} />
        <KpiCard label="🔄 Em andamento" value={kpis.andamento} color="#3b82f6" active={statusFilter === "andamento"} onClick={() => setStatusFilter("andamento")} />
        <KpiCard label="⏳ Pendentes" value={kpis.pend} color="#f59e0b" active={statusFilter === "pendente"} onClick={() => setStatusFilter("pendente")} />
        <KpiCard label="🚫 Sem interesse" value={kpis.semInteresse} color="#d97706" active={statusFilter === "sem_interesse"} onClick={() => setStatusFilter("sem_interesse")} />
        <KpiCard label="📅 Criadas este mês" value={kpis.mesCount} color="#0d9488" />
      </div>

      {/* ── Barra de ações ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => setShowFilters((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: `1px solid ${filtrosAtivos ? "#7c3aed" : "#d1d5db"}`, background: filtrosAtivos ? "#f5f3ff" : "#fff", color: filtrosAtivos ? "#6d28d9" : "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Filter size={13} /> Filtros{filtrosAtivos ? " •" : ""}
          </button>
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ ...sel, width: "auto" }}>
            <option value="recente">Mais recentes</option>
            <option value="antigo">Mais antigos</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={handleExport} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Download size={13} /> CSV
          </button>
          <button onClick={() => setVerTabelas(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }} title="Taxas, prazos, maquininha e pagamento do combustível, por região ou fornecedor">
            <Percent size={13} /> Tabelas comerciais
          </button>
          <TabelasComerciais open={verTabelas} onClose={() => setVerTabelas(false)} />
          {/* Empresa nova nasce na Rede de Parceiros, onde o contato acontece —
              e onde o registro rápido agora exige a prova do contato. Criar
              ficha por aqui pulava esse passo e era de onde vinha boa parte das
              empresas duplicadas e sem conversa nenhuma registrada. */}
          <button onClick={onIrParaRede} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #7c3aed", background: "#f5f3ff", color: "#6d28d9", fontSize: 12, fontWeight: 600, cursor: "pointer" }} title="Registro rápido, formulário e nova empresa ficam na Rede de Parceiros">
            <ArrowRight size={13} /> Cadastrar na Rede de Parceiros
          </button>
        </div>
      </div>

      {/* ── Quadro de filtros ── */}
      {showFilters && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div><Lbl>Buscar (nome, CNPJ)</Lbl><input style={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empresa, CNPJ..." /></div>
            <div><Lbl>Status</Lbl><select style={sel} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>{STATUS_OPTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
            <div><Lbl>Sistema</Lbl><select style={sel} value={sistemaFilter} onChange={(e) => setSistemaFilter(e.target.value)}><option value="all">Todos</option>{SISTEMAS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
            <div><Lbl>Estado (UF)</Lbl><select style={sel} value={ufFilter} onChange={(e) => setUfFilter(e.target.value)}><option value="all">Todos</option>{(ufOptions.length ? ufOptions : BRAZILIAN_STATES.map((s) => s.uf)).map((uf) => <option key={uf} value={uf}>{uf}</option>)}</select></div>
            <div><Lbl>Cidade</Lbl><input style={inp} value={cidadeFilter} onChange={(e) => setCidadeFilter(e.target.value)} placeholder="Cidade" /></div>
            <div><Lbl>Responsável</Lbl><input style={inp} value={respFilter} onChange={(e) => setRespFilter(e.target.value)} placeholder="Nome do responsável" /></div>
            <div><Lbl>Serviço prestado</Lbl><select style={sel} value={espFilter} onChange={(e) => setEspFilter(e.target.value)}><option value="all">Todos</option>{SERVICOS_PRESTADOS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
            <div><Lbl>Socorro / assistência</Lbl><select style={sel} value={socorroFilter} onChange={(e) => setSocorroFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="sim">Presta socorro/assistência</option>
              <option value="24h">Atende 24 horas</option>
              <option value="rodovias">Atende em rodovias</option>
              <option value="nao">Não presta</option>
            </select></div>
            <div><Lbl>Data de criação</Lbl><select style={sel} value={periodoFilter} onChange={(e) => setPeriodoFilter(e.target.value)}>{PERIODOS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select></div>
          </div>
          {filtrosAtivos && (
            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>{filtered.length} resultado(s)</span>
              <button onClick={limparFiltros} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}><X size={12} /> Limpar filtros</button>
            </div>
          )}
        </div>
      )}

      {/* ── Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 10 }}>
        {paginated.length === 0 && (
          <div style={{ gridColumn: "1/-1", padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            <ClipboardCheck size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
            <div>Nenhuma ficha encontrada.</div>
          </div>
        )}
        {paginated.map((c) => {
          const st = credStatus(c);
          const wa = waLink(c.telefone);
          const concluida = st.id === "concluido";
          return (
            <div key={c.id} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb", borderLeft: `4px solid ${st.color}` }}>
              <div onClick={() => setDetailOf(c)} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.nomeFantasia || c.razaoSocial || "Sem nome"}
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>
                    {TIPOS_ESTABELECIMENTO.find((t) => t.id === c.tipoEstabelecimento)?.label || "Estabelecimento"}{c.cnpj ? ` · ${c.cnpj}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                  <Badge color={st.color}>{st.label}</Badge>
                  {/* De que empresa da rede esta ficha é, e onde ela está no
                      funil. Sem isto, a esteira documental não sabe de quem
                      está tratando. */}
                  {(() => {
                    const p = parceiroDaFicha[c.id];
                    const e = p && PARTNER_STAGES.find((x) => x.id === p.stage);
                    if (!e) return null;
                    return (
                      <span
                        onClick={(ev) => { ev.stopPropagation(); onVerNaRede && onVerNaRede(p); }}
                        title="Ver esta empresa na Rede de Parceiros"
                        style={{ fontSize: 9, fontWeight: 700, color: e.color, background: `${e.color}14`, padding: "1px 6px", borderRadius: 10, cursor: onVerNaRede ? "pointer" : "default", whiteSpace: "nowrap" }}
                      >
                        {e.label}
                      </span>
                    );
                  })()}
                  {/* Mesmo selo da Rede: existe o export do WhatsApp desta
                      empresa — a conversa dá para ler, não é só palavra. */}
                  {conversaValidada(parceiroDaFicha[c.id], c).ok && (
                    <span title="Conversa exportada do WhatsApp anexada" style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, fontWeight: 700, color: "#0f766e", background: "#f0fdfa", padding: "1px 6px", borderRadius: 10 }}>
                      <ShieldCheck size={9} /> CONVERSA VALIDADA
                    </span>
                  )}
                  {c.origem === "link" && <span style={{ fontSize: 9, fontWeight: 700, color: "#6d28d9", background: "#f5f3ff", padding: "1px 6px", borderRadius: 10 }}>VIA LINK</span>}
                  {c.atende24h && <span style={{ fontSize: 9, fontWeight: 700, color: "#b91c1c", background: "#fef2f2", padding: "1px 6px", borderRadius: 10 }}>SOCORRO 24H</span>}
                  {c.fazSocorro && !c.atende24h && <span style={{ fontSize: 9, fontWeight: 700, color: "#b45309", background: "#fffbeb", padding: "1px 6px", borderRadius: 10 }}>SOCORRO</span>}
                </div>
              </div>

              {c.responsavelNome && <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>Resp.: {c.responsavelNome}{c.responsavelDoc ? ` — ${c.responsavelDoc}` : ""}</div>}
              <div style={{ fontSize: 11, color: "#475569", marginTop: 3 }}><MapPin size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{[c.cidade, c.estado].filter(Boolean).join("/") || "—"}</div>
              {(c.email || c.celular || c.telefone) && <div style={{ fontSize: 11, color: "#475569", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[c.email, c.celular || c.telefone].filter(Boolean).join(" · ")}</div>}

              {/* Pendências */}
              {!concluida && st.pend.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                  <AlertCircle size={11} color="#f59e0b" />
                  <span style={{ fontSize: 10, color: "#b45309" }}>Falta: {st.pend.join(", ")}</span>
                </div>
              )}
              {/* Documentos da empresa: cobram, mas não travam. Fundo azul (e não
                  âmbar) porque isto é a 2ª etapa seguindo seu curso — não é a
                  ficha com problema. */}
              {st.docsPend?.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, padding: "5px 8px", borderRadius: 6, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                  <Paperclip size={11} color="#1d4ed8" />
                  <span style={{ fontSize: 10, color: "#1d4ed8", fontWeight: 600 }}>Pedir depois: {st.docsPend.map((d) => d.label).join(", ")}</span>
                </div>
              )}

              {/* Sistemas */}
              <div style={{ display: "flex", gap: 3, marginTop: 8, flexWrap: "wrap" }}>
                {(c.sistemas || []).map((id) => { const s = SISTEMAS.find((x) => x.id === id); return s ? <Badge key={id} color={s.color} small>{s.label}</Badge> : null; })}
              </div>
              </div>

              {/* Ações rápidas */}
              <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
                {concluida ? (
                  <button onClick={() => onEdit(c)} style={actBtn(false)} title="Editar"><Pencil size={12} /> Editar</button>
                ) : (
                  <button onClick={() => onEdit(c)} style={{ ...actBtn(false), color: "#b45309", borderColor: "#fcd34d", background: "#fffbeb" }} title="Continuar cadastro"><ArrowRight size={12} /> Continuar</button>
                )}
                <button onClick={() => wa && window.open(wa, "_blank")} disabled={!wa} style={{ ...actBtn(!wa), color: wa ? "#059669" : "#cbd5e1", borderColor: wa ? "#a7f3d0" : "#e2e8f0" }} title={wa ? "WhatsApp" : "Sem telefone"}><MessageCircle size={12} /></button>
                {/* "Ver ficha" e "Baixar PDF" chamavam o MESMO openFile e abriam a
                    mesma aba — dois botões para uma ação só. Ficou um. */}
                <button onClick={() => openFile(c.fichaPath)} disabled={!c.fichaPath} style={actBtn(!c.fichaPath)} title="Ver ficha (PDF)"><FileText size={12} /></button>
                {(c.documentos || []).length > 0 && (
                  <button onClick={() => openFile(c.documentos[0].path)} style={actBtn(false)} title={`Documentos anexados (${c.documentos.length})`}><ImageIcon size={12} /></button>
                )}
                {/* Cobrar documento é o que mais se faz nesta tela; antes exigia
                    abrir a ficha para achar o link que a integração já trouxe. */}
                {(() => {
                  const pend = pendenciaDoFornecedor(c);
                  if (!pend?.url) return null;
                  const tel = String(c.celular || c.telefone || "").replace(/\D/g, "");
                  const base = tel ? `https://wa.me/${tel.length <= 11 ? "55" + tel : tel}` : "https://wa.me/";
                  const texto = `Olá! Para concluir seu credenciamento na InstaSolutions, envie os documentos por aqui (o link expira em 7 dias):\n${pend.url}`;
                  return (
                    <button
                      onClick={() => window.open(`${base}?text=${encodeURIComponent(texto)}`, "_blank")}
                      style={{ ...actBtn(false), color: "#6d28d9", borderColor: "#ddd6fe", background: "#f5f3ff" }}
                      title={`Faltam: ${pend.faltam.map((d) => d.label || d.tipo).join(", ")}`}
                    >
                      <Paperclip size={12} /> Cobrar {pend.faltam.length} doc.
                    </button>
                  );
                })()}
                <button onClick={() => setHistOf(c)} style={actBtn(false)} title="Histórico"><History size={12} /></button>
                <button onClick={() => setToDelete(c)} style={{ ...actBtn(false), marginLeft: "auto", color: "#ef4444", borderColor: "#fecaca" }} title="Excluir"><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb" }}>
        <Pagination page={page} totalItems={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>

      {/* Histórico */}
      <Modal open={!!histOf} onClose={() => setHistOf(null)} title={`Histórico — ${histOf?.nomeFantasia || histOf?.razaoSocial || "Ficha"}`} w={520}>
        {(() => {
          const h = [...(histOf?.historico || [])].reverse();
          if (!h.length) return <div style={{ fontSize: 13, color: "#94a3b8", padding: 8 }}>Sem eventos registrados nesta ficha.</div>;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {h.map((ev, i) => (
                <div key={i} style={{ display: "flex", gap: 10, paddingBottom: 14, position: "relative" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: ACAO_COLOR[ev.acao] || "#94a3b8", flexShrink: 0, marginTop: 3 }} />
                    {i < h.length - 1 && <div style={{ width: 2, flex: 1, background: "#e5e7eb", marginTop: 2 }} />}
                  </div>
                  <div style={{ paddingBottom: 2 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{ACAO_LABEL[ev.acao] || ev.acao}</div>
                    {ev.detalhe && <div style={{ fontSize: 11, color: "#64748b" }}>{ev.detalhe}</div>}
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{fmtDataHora(ev.ts)}{ev.autor ? ` · ${ev.autor}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>

      {/* Detalhes da ficha */}
      {/* O detalhe agora é um componente só, compartilhado com a aba Rede de
          Parceiros: era o que ainda obrigava a equipe a trocar de tela para
          pedir documento ou emitir o Cartão CNPJ. */}
      <FichaDetalhe
        ficha={detailOf ? credenciamentos.find((x) => x.id === detailOf.id) || detailOf : null}
        onClose={() => setDetailOf(null)}
        setCredenciamentos={setCredenciamentos}
        onEdit={onEdit}
        onExcluir={(c) => setToDelete(c)}
        userName={userName}
      />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        danger
        confirmLabel="Excluir"
        title="Excluir ficha"
        message={`Excluir a ficha de "${toDelete?.nomeFantasia || toDelete?.razaoSocial || ""}"? Os arquivos (PDF e documentos) serão removidos.`}
      />

    </div>
  );
}

function KpiCard({ label, value, color, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff", borderRadius: 10, padding: "12px 14px",
        border: `1px solid ${active ? color : "#e5e7eb"}`,
        borderLeft: `4px solid ${color}`, cursor: onClick ? "pointer" : "default",
        boxShadow: active ? `0 0 0 1px ${color}` : "none",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function Lbl({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{children}</div>;
}


const actBtn = (disabled) => ({
  display: "flex", alignItems: "center", gap: 4, padding: "6px 10px",
  borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff",
  color: disabled ? "#cbd5e1" : "#475569", fontSize: 11, fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
});
